import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import Button from '@adsk/bim360-matrix-react-button';
import InfoPopover from '@adsk/bim360-matrix-react-info-popover';
import {
  default as TextInput,
  Behaviors as TextInputBehaviors,
  InputUITypes,
  ValidationTypes
} from '@adsk/bim360-matrix-react-text-input';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import StickyScrollableContainer from '@adsk/bim360-matrix-react-sticky-scrollable-container';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import AnimateHeightPopUp from '../../common/AnimateHeightPopUp';
import IssueTypeStatusDropdown from './IssueTypeStatusDropdown';
import IssueTypeStatusSetDropdown from './IssueTypeStatusSetDropdown';
import IssueAttributesAdminTable from '../issue-attributes/IssueAttributesAdminTable';
import IssueRequiredAttributesAdminTableContainer from '../issue-attributes/IssueRequiredAttributesAdminTableContainer';
import {
  isImmediateSubType,
  isStandardType,
  issueTypeInputValidators,
  subIssueTypeInputValidators
} from '../../../utils/issueTypes-helpers';
import {ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES} from '../../../constants/issue-attributes';
import cx from 'classnames';
import {ISSUE_ALL_TYPES_UPDATE_ACTIONS, ISSUE_TYPES_UPDATE_ACTIONS} from '../../../constants/issue-types';
import IssueTypesAdminChangeActiveStatusModal from './IssueTypesAdminChangeActiveStatusModal';

export default class IssueTypeEditModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = props.isOpen ? this.getInitializedState(props) : {};

    this.infoIconSize = '14px';
    this.titleInputProps = {
      className: 'IssueTypeEditModal__title-text-field',
      'data-testid': 'IssueTypeEditModal__title-textfield',
      autofocus: true,
      behavior: TextInputBehaviors.SINGLE_LINE,
      onChange: title => {
        this.setState({title});
        this.props.updatePendingIssueTypeAction({title});
      },
      onInlineEditStart: () => {
        this.setState({isTitleValid: false});
      },
      onInlineEditEnd: (title, isCancel, previousValue, finisher, valid) => {
        const changeObj = {isTitleValid: valid};
        if (isCancel || !title.trim().length) {
          changeObj.title = previousValue;
        }
        this.setState(changeObj);
      }
    };

    this.closeModalAndOpenCreateAttributeModal = this.closeModalAndOpenCreateAttributeModal.bind(this, true);
    this.saveIssueType = this.saveIssueType.bind(this);
    this.onStatusChange = this.onStatusChange.bind(this);
    this.removeIssueAttributeMapping = this.removeIssueAttributeMapping.bind(this);
    this.closeAnswerRemoveConfirmationPopup = this.closeAnswerRemoveConfirmationPopup.bind(this);
    this.removeAnswerAfterConfirmation = this.removeAnswerAfterConfirmation.bind(this);
    this.updateActionStatusAndCloseModal = this.updateActionStatusAndCloseModal.bind(this);
    this.renderModalTitle = this._renderDecorator(this.renderModalTitle);
    this.renderScrollableContainer = this._renderDecorator(this.renderScrollableContainer);
    this.renderFooter = this._renderDecorator(this.renderFooter);
    this.renderStatusSetDropdown = this._renderDecorator(this.renderStatusSetDropdown);
  }

  componentWillReceiveProps(nextProps) {
    const nextIssueTypeId = nextProps.issueTypeForEdit && nextProps.issueTypeForEdit.id;
    const currentIssueTypeId = this.props.issueTypeForEdit && this.props.issueTypeForEdit.id;
    if (nextIssueTypeId) {
      if (nextIssueTypeId !== currentIssueTypeId) {
        // Different issue type, reset all
        this.setState(this.getInitializedState(nextProps));
      } else if (!this.props.isOpen && nextProps.isOpen) {
        // Coming back from related modals, don't reset edited properties
        this.setState(this.getModalStateInitialized());
      }
    }
  }

  // This component always renders and we're using the `show` prop of SimpleModal to actually show it in the DOM or hide it.
  // However, because we're moving actual nodes to SimpleModal we need to guard all of them because the modal might be not opened (meaning no issue type selected)
  // Therefore we're using the renderDecorator for this sole purpose.
  _renderDecorator(renderFunc) {
    return () => {
      if (!this.props.isOpen) {
        return null;
      }
      return renderFunc.call(this);
    };
  }

  getInitializedState(props) {
    return {
      ...this.getModalStateInitialized(),
      ...this.getIssueTypeEditStateInitialized(props)
    };
  }

  getModalStateInitialized() {
    return {
      issueAttributeToRemove: null,
      showRemoveAttributeConfirmation: false,
      changeActiveStatusModalOpen: false,
      ...this.getUpdateActionsStatusInitialized()
    };
  }

  getIssueTypeEditStateInitialized(props) {
    return {
      title: props.issueTypeForEdit.title,
      titleValidationProps: this.getTitleValidationProps(props),
      titleEditableProps: this.getTitleEditableProps(this.isReadOnlyIssueType(props), !!props.issueTypeForEditParent),
      isTitleValid: true,
      status: props.issueTypeForEdit.isActive
        ? IssueTypeStatusDropdown.IssueTypeStatuses.ACTIVE
        : IssueTypeStatusDropdown.IssueTypeStatuses.INACTIVE,
      statusSet: props.issueTypeForEditParent
        ? {value: props.issueTypeForEditParent.statusSet}
        : {value: props.issueTypeForEdit.statusSet}
    };
  }

  getUpdateActionsStatusInitialized() {
    return this._getUpdateActionKeys().reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
  }

  closeModalAndOpenCreateAttributeModal() {
    if (this.state.isTitleValid) {
      // Prevent changes in attributes (that can lead to saving), if title is invalid
      this.props.closeModal({shouldOpenAddAttributesModal: true});
    }
  }

  isReadOnlyIssueType(props = this.props) {
    return isStandardType(props.issueTypeForEdit) || this._isContainerType();
  }

  getTitleValidationProps(props) {
    return {
      type: ValidationTypes.POPOVER,
      required: true,
      validator: props.issueTypeForEditParent
        ? subIssueTypeInputValidators(props.issueTypeNames)
        : issueTypeInputValidators(props.issueTypeNames),
      typeProps: {
        container: this.props.container
      }
    };
  }

  getTitleEditableProps(isReadOnly, isSubtype) {
    return {
      readOnly: isReadOnly,
      'data-testid': 'IssueTypeEditModal__title-editable',
      className: cx('IssueTypeEditModal__title-editable', {
        'IssueTypeEditModal__title-editable--subtype': isSubtype
      }),
      multiline: false
    };
  }

  onStatusChange(status) {
    if (this.props.ffShowDisableTemplatesModal && status === IssueTypeStatusDropdown.IssueTypeStatuses.INACTIVE) {
      this.setState({changeActiveStatusModalOpen: true});
    } else {
      this.setState({status});
      this.props.updatePendingIssueTypeAction({
        isActive: status === IssueTypeStatusDropdown.IssueTypeStatuses.ACTIVE
      });
    }
  }

  renderModalTitle() {
    const {issueTypeForEditParent} = this.props;

    if (this._isContainerType()) {
      return i18n.t('issueTypes.modal.editAllTypesTitle');
    }

    const editableTitle = (
      <TextInput
        inputUIType={InputUITypes.INLINE_EDIT}
        inputProps={this.titleInputProps}
        validationProps={this.state.titleValidationProps}
        editableProps={this.state.titleEditableProps}
        value={this.state.title}
      />
    );

    return (
      <div className="IssueTypeEditModal__title-container">
        {i18n.t('issueTypes.modal.editTitlePrefix')}
        {issueTypeForEditParent && (
          <span className="IssueTypeEditModal__title-parent">{issueTypeForEditParent.title}</span>
        )}
        {issueTypeForEditParent && <SvgIcon svgId="menu-arrow" className="IssueTypeEditModal__title-arrow-separator" />}
        {editableTitle}
      </div>
    );
  }

  renderActiveDropdown() {
    if (this.props.issueTypeForEdit.isContainerType) {
      return null;
    }

    const isAnImmediateSubType = isImmediateSubType(this.props.issueTypeForEdit, this.props.issueTypeForEditParent);
    return [
      <div key="status-title" className="IssueTypeEditModal__sub-title-container">
        <span className="IssueTypeEditModal__sub-title">{i18n.t('issueTypes.modal.activeInactive')}</span>
        {isAnImmediateSubType && (
          <span className="IssueTypeEditModal__status-inherited">({i18n.t('issueTypes.modal.inherited')})</span>
        )}
      </div>,
      <IssueTypeStatusDropdown
        key="status-dropdown"
        disabled={isAnImmediateSubType}
        onChange={this.onStatusChange}
        selected={this.state.status}
      />
    ];
  }

  renderStatusSetDropdown() {
    if (this._isContainerType()) {
      return null;
    }

    return [
      <div key="statusSet-title" className="IssueTypeEditModal__sub-title-container">
        <span className="IssueTypeEditModal__sub-title">{i18n.t('issueTypes.header.statusSet')}</span>
      </div>,
      <IssueTypeStatusSetDropdown key="statusSet-dropdown" selected={this.state.statusSet} disabled />
    ];
  }

  removeIssueAttributeMapping(issueAttributeToRemove) {
    if (!isNullOrUndefined(issueAttributeToRemove)) {
      this.setState({
        issueAttributeToRemove,
        showRemoveAttributeConfirmation: true,
        titleEditableProps: this.getTitleEditableProps(true, !!this.props.issueTypeForEditParent)
      });
    }
  }

  closeAnswerRemoveConfirmationPopup() {
    const isReadOnlyIssueType = this.isReadOnlyIssueType();
    this.setState({
      showRemoveAttributeConfirmation: false,
      issueAttributeToRemove: null,
      titleEditableProps: this.getTitleEditableProps(isReadOnlyIssueType, !!this.props.issueTypeForEditParent)
    });
  }

  removeAnswerAfterConfirmation() {
    this.props.removeIssueAttributeMappingAction(this.state.issueAttributeToRemove.id);
    this.closeAnswerRemoveConfirmationPopup();
  }

  renderCustomAttributesTable() {
    const editModalMappedItemType = !isNullOrUndefined(this.props.issueTypeForEdit.parentId)
      ? ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.SUBTYPE
      : ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.TYPE;
    return [
      <div key="table-title" className="IssueTypeEditModal__sub-title-container">
        <span className="IssueTypeEditModal__sub-title">{i18n.t('issueTypes.modal.customIssueAttributes')}</span>
        <InfoPopover
          tooltipClassName="IssueTypeEditModal__attributes-info"
          message={i18n.t('issueTypes.modal.customAttributesInfo')}
          iconWidth={this.infoIconSize}
          iconHeight={this.infoIconSize}
          container={this.props.container}
        />
      </div>,
      <div key="attributes-table" className="IssueTypeEditModal__attributes-table">
        <IssueAttributesAdminTable
          tableData={this.props.issueAttributes}
          inheritedIssueAttributes={this.props.inheritedIssueAttributes}
          removeIssueAttributeMapping={this.removeIssueAttributeMapping}
          onMappingRequiredChange={this.props.setSelectedIssueTypeMappingRequiredAction}
          editModalMode
          editModalMappedItemType={editModalMappedItemType}
          openCreateAttributeModal={this.closeModalAndOpenCreateAttributeModal}
          issueTypeAttributeMappings={this.props.issueTypeAttributeMappings}
        />
      </div>
    ];
  }

  renderInheritInfoMessage() {
    const numOfSubTypes = this.props.issueTypeForEdit.childrenIds.length;
    const message = this._isContainerType()
      ? i18n.t('issueTypes.modal.allTypesWillInherit')
      : numOfSubTypes > 1
        ? i18n.t('issueTypes.modal.subTypesWillInherit', {numOfSubTypes})
        : i18n.t('issueTypes.modal.subTypeWillInherit', {numOfSubTypes});

    return (
      <div className="IssueTypeEditModal__inherit-info-container">
        <SvgIcon
          svgId="info-circle-small"
          className="IssueTypeEditModal__inherit-info-icon"
          width={this.infoIconSize}
          height={this.infoIconSize}
        />
        {message}
      </div>
    );
  }

  updateActionStatusAndCloseModal(action) {
    this.setState(
      {
        [action]: true
      },
      () => {
        if (this._getUpdateActionKeys().every(key => this.state[key] === true)) {
          this.props.showIssueTypeUpdatedNotificationAction(!!this.props.issueTypeForEditParent);
          this.props.closeModal();
        }
      }
    );
  }

  saveIssueType() {
    const onSuccessUpdateIssueType = () => {
      this.updateActionStatusAndCloseModal(ISSUE_TYPES_UPDATE_ACTIONS.ISSUE_TYPE);
    };
    const onSuccessUpdateIssueTypeMappings = () => {
      this.updateActionStatusAndCloseModal(ISSUE_TYPES_UPDATE_ACTIONS.ISSUE_TYPE_MAPPINGS);
    };

    this.props.updateIssueTypeAction(onSuccessUpdateIssueType, onSuccessUpdateIssueTypeMappings);

    if (this._isContainerType()) {
      this.props.serverUpdateRequiredFields({
        modalCallback: () => {
          this.updateActionStatusAndCloseModal(ISSUE_ALL_TYPES_UPDATE_ACTIONS.REQUIRED_FIELDS);
        }
      });
    }
  }

  _getUpdateActionKeys() {
    const updateActionKeys = [ISSUE_TYPES_UPDATE_ACTIONS.ISSUE_TYPE, ISSUE_TYPES_UPDATE_ACTIONS.ISSUE_TYPE_MAPPINGS];

    if (this._isContainerType()) {
      updateActionKeys.push(ISSUE_ALL_TYPES_UPDATE_ACTIONS.REQUIRED_FIELDS);
    }

    return updateActionKeys;
  }
  /** whether we're editing a specific issue type, or the general settings for all types */
  _isContainerType() {
    return this.props.issueTypeForEdit?.isContainerType;
  }

  renderConfirmationPopup() {
    const {showRemoveAttributeConfirmation, issueAttributeToRemove} = this.state;
    const title = issueAttributeToRemove ? issueAttributeToRemove.title : '';
    return (
      <AnimateHeightPopUp
        isOpen={showRemoveAttributeConfirmation}
        className="IssueTypeEditModal__animated-confirmation"
      >
        <div className="IssueTypeEditModal__confirmation" data-testid="IssueTypeEditModal__confirmation">
          <div className="IssueTypeEditModal__confirmation-text">
            <div
              data-testid="IssueTypeEditModal__confirmation-title"
              className="IssueTypeEditModal__confirmation-title"
            >
              {i18n.t('issueTypes.modal.removeAttributeTitle', {issueAttributeName: title})}
            </div>
            <div
              data-testid="IssueTypeEditModal__confirmation-message"
              className="IssueTypeEditModal__confirmation-message"
            >
              {i18n.t('issueTypes.modal.removeAttributeWarning')}
            </div>
          </div>
          <div className="IssueTypeEditModal__confirmation-footer">
            <Button
              styleType={Button.StyleType.LINK_SECONDARY}
              onClick={this.closeAnswerRemoveConfirmationPopup}
              className="IssueTypeEditModal__button IssueTypeEditModal__button--cancel"
              tabIndex="0"
              data-testid="IssueTypeEditModal__button--cancel"
            >
              {i18n.t('issueTypes.modal.cancel')}
            </Button>
            <Button
              styleType={Button.StyleType.DANGER}
              onClick={this.removeAnswerAfterConfirmation}
              className="IssueTypeEditModal__button IssueTypeEditModal__button--confirm"
              tabIndex="0"
              data-testid="IssueTypeEditModal__button--confirm"
            >
              {i18n.t('issueTypes.modal.remove')}
            </Button>
          </div>
        </div>
      </AnimateHeightPopUp>
    );
  }

  renderFooter() {
    const {saveInProgress} = this.props;
    const {showRemoveAttributeConfirmation} = this.state;
    const isDisabled = saveInProgress || showRemoveAttributeConfirmation;
    return (
      <div className="IssueTypeEditModal__footer">
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          className="IssueTypeEditModal__footer-button"
          onClick={this.props.closeModal}
          disabled={isDisabled}
          data-testid="IssueTypeEditModal__cancel-button"
        >
          {i18n.t('issueTypes.modal.cancel')}
        </Button>
        <Button
          styleType={Button.StyleType.PRIMARY}
          className="IssueTypeEditModal__footer-button"
          onClick={this.saveIssueType}
          disabled={isDisabled || !this.state.isTitleValid}
          data-testid="IssueTypeEditModal__save-button"
        >
          {saveInProgress ? i18n.t('issueTypes.modal.saving') : i18n.t('issueTypes.modal.save')}
        </Button>
      </div>
    );
  }
  _renderChangeActiveStatusModal() {
    return (
      <IssueTypesAdminChangeActiveStatusModal
        container={this}
        isOpen={this.state.changeActiveStatusModalOpen}
        onModalClose={() => this.setState({changeActiveStatusModalOpen: false})}
        onConfirm={() => {
          this.setState({
            changeActiveStatusModalOpen: false,
            status: IssueTypeStatusDropdown.IssueTypeStatuses.INACTIVE
          });
          this.props.updatePendingIssueTypeAction({isActive: false});
        }}
      />
    );
  }

  renderScrollableContainer() {
    const stickyClass = cx('IssueTypeEditModal__sticky', {
      'IssueTypeEditModal__sticky--fixed': this.state.issueAttributeToRemove !== null
    });

    return (
      <StickyScrollableContainer className={stickyClass}>
        <StickyScrollableContainer.Header>{this.renderConfirmationPopup()}</StickyScrollableContainer.Header>
        <StickyScrollableContainer.Content className="IssueTypeEditModal__scrollable-container">
          {this.renderActiveDropdown()}
          {this.renderStatusSetDropdown()}
          {this._isContainerType() && (
            <>
              <div key="table-title" className="IssueTypeEditModal__sub-title-container">
                <span className="IssueTypeEditModal__sub-title">{i18n.t('issueTypes.modal.requiredFields')}</span>
                <InfoPopover
                  tooltipClassName="IssueTypeEditModal__attributes-info"
                  message={i18n.t('issueTypes.modal.requiredFieldsInfo')}
                  iconWidth={this.infoIconSize}
                  iconHeight={this.infoIconSize}
                  container={this.props.container}
                />
              </div>
              <div key="attributes-table" className="IssueTypeEditModal__attributes-table">
                <IssueRequiredAttributesAdminTableContainer />
              </div>
            </>
          )}
          {this.renderCustomAttributesTable()}
          {!this.props.issueTypeForEditParent && this.renderInheritInfoMessage()}
          {this.props.ffShowDisableTemplatesModal && this._renderChangeActiveStatusModal()}
        </StickyScrollableContainer.Content>
      </StickyScrollableContainer>
    );
  }

  render() {
    return (
      <SimpleModal
        className="IssueTypeEditModal"
        show={this.props.isOpen}
        container={this.props.container}
        title={this.renderModalTitle()}
        onHide={() => this.props.closeModal()}
        onExited={() => this.props.closeModal({isExited: true})}
        footer={this.renderFooter()}
        modalBodyProps={{className: 'IssueTypeEditModal__modal-body'}}
        disableModalExit={this.props.saveInProgress || this.state.issueAttributeToRemove}
      >
        {this.renderScrollableContainer()}
      </SimpleModal>
    );
  }
}

IssueTypeEditModal.displayName = 'IssueTypeEditModal';

IssueTypeEditModal.propTypes = {
  container: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  issueTypeForEdit: PropTypes.object,
  issueTypeForEditParent: PropTypes.object,
  issueTypeNames: PropTypes.arrayOf(PropTypes.string),
  saveInProgress: PropTypes.bool.isRequired,
  updateIssueTypeAction: PropTypes.func.isRequired,
  updatePendingIssueTypeAction: PropTypes.func.isRequired,
  removeIssueAttributeMappingAction: PropTypes.func.isRequired,
  showIssueTypeUpdatedNotificationAction: PropTypes.func.isRequired,
  issueAttributes: PropTypes.arrayOf(PropTypes.object).isRequired,
  inheritedIssueAttributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  issueTypeAttributeMappings: PropTypes.arrayOf(PropTypes.object),
  setSelectedIssueTypeMappingRequiredAction: PropTypes.func.isRequired,
  serverUpdateRequiredFields: PropTypes.func.isRequired,
  ffShowDisableTemplatesModal: PropTypes.bool.isRequired
};
